/**
 * Mobile Performance Optimizations
 * Lazy loads heavy third-party scripts and optimizes page load
 */

(function () {
  'use strict';

  // Check if mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  /**
   * Lazy load images that are not yet visible
   */
  function optimizeImages() {
    // Add loading="lazy" to images without it
    const images = document.querySelectorAll('img:not([loading])');
    images.forEach((img, index) => {
      // First 3 images load eagerly (above fold)
      if (index < 3) {
        img.setAttribute('loading', 'eager');
        if (!img.hasAttribute('fetchpriority')) {
          img.setAttribute('fetchpriority', 'high');
        }
      } else {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      }
    });

    // Handle background images
    const bgElements = document.querySelectorAll('[style*="background-image"]');
    if ('IntersectionObserver' in window) {
      const bgObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bg-visible');
            bgObserver.unobserve(entry.target);
          }
        });
      });
      bgElements.forEach(el => bgObserver.observe(el));
    }
  }

  /**
   * Boost LCP: Aggressively load the hero image immediately.
   * Lazysizes uses 'lazyloadt4s' / 'lazyloadt4sed' classes to control loading.
   * We MUST remove these classes or lazysizes will override fetchpriority and defer the image.
   */
  function boostLCP() {
    const heroSelectors = [
      '.flickityt4s-slider img',
      '.t4s-slideshow img',
      '.t4s-hero img',
      '.t4s-banner img',
      '#MainContent .t4s-section:first-child img',
      '#MainContent img'
    ];

    let heroImg = null;
    for (const sel of heroSelectors) {
      heroImg = document.querySelector(sel);
      if (heroImg) break;
    }

    if (!heroImg) return;

    // Step 1: Remove ALL lazysizes classes so it won't be deferred
    heroImg.classList.remove('lazyloadt4s', 'lazyloadt4sed', 'lazyload', '-t4s-lz--fadeIn', 't4s-slide-none');

    // Step 2: Force eager loading + highest priority
    heroImg.setAttribute('fetchpriority', 'high');
    heroImg.setAttribute('loading', 'eager');
    heroImg.removeAttribute('decoding');

    // Step 3: If src is still data-src / not yet loaded, resolve it immediately
    if (heroImg.dataset.src && (!heroImg.src || heroImg.src.indexOf('width=1') !== -1)) {
      heroImg.src = heroImg.dataset.src;
    }

    // Step 4: Resolve srcset from data-srcset if present
    if (heroImg.dataset.srcset && !heroImg.srcset) {
      heroImg.srcset = heroImg.dataset.srcset;
    }

    // Step 5: Apply sizes attribute
    if (heroImg.dataset.sizes && !heroImg.sizes) {
      heroImg.sizes = heroImg.dataset.sizes;
    }

    // Step 6: Tell lazysizes this image is already loaded (prevents re-processing)
    heroImg.setAttribute('data-ll-status', 'loaded');
  }

  /**
   * Defer non-critical CSS
   */
  function deferNonCriticalCSS() {
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"][data-defer]');
    stylesheets.forEach(link => {
      link.setAttribute('media', 'print');
      link.onload = function () {
        this.media = 'all';
      };
    });
  }

  /**
   * Trigger deferred styles
   */
  function loadDeferredStyles() {
    const deferredStyles = [
      '#line-awesome-css',
      '#font-awesome-css'
    ];

    deferredStyles.forEach(selector => {
      const link = document.querySelector(selector);
      if (link && link.getAttribute('media') === 'print') {
        link.setAttribute('media', 'all');
      }
    });
  }

  /**
   * Lazy load analytics + heavy scripts
   */
  let scriptsLoaded = false;
  function loadHeavyScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    // Load deferred styles first
    loadDeferredStyles();

    // Conditionally load polyfills only for older browsers
    const needsPolyfills = !('IntersectionObserver' in window) ||
      !('fetch' in window) ||
      !window.Promise ||
      !window.URLSearchParams;

    if (needsPolyfills && window.T4Sconfigs && window.T4Sconfigs.script1) {
      const polyfillScript = document.createElement('script');
      polyfillScript.src = window.T4Sconfigs.script1;
      polyfillScript.defer = true;
      document.head.appendChild(polyfillScript);
    }

    // Load GTM (deferred analytics)
    if (typeof window.loadGTM === 'function') {
      window.loadGTM();
    }

    // Load GA4 (deferred analytics) — avoids blocking TBT
    if (typeof window.loadGA4 === 'function') {
      window.loadGA4();
    }

    // Signal other deferred scripts to load
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('scripts:lazyload'));
    }, 100);
  }

  /**
   * Setup intersection observer for lazy loading components
   */
  function setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const lazyElements = document.querySelectorAll('[data-lazy-load]');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const src = element.dataset.src;

          if (src) {
            if (element.tagName === 'IMG') {
              element.src = src;
            } else if (element.tagName === 'IFRAME') {
              element.src = src;
            }

            element.removeAttribute('data-lazy-load');
            observer.unobserve(element);
          }
        }
      });
    }, {
      rootMargin: '100px'
    });

    lazyElements.forEach(el => observer.observe(el));
  }

  /**
   * Optimize third-party scripts on mobile
   * Delays load until user interaction or 3s idle
   */
  function optimizeThirdPartyScripts() {
    const isBot = /bot|googlebot|crawler|spider|lighthouse|pagespeed/i.test(navigator.userAgent);
    // For bots/Lighthouse: load quickly so they can measure analytics
    // For users: wait for interaction first (better UX + TBT)
    let idleTimer = setTimeout(loadHeavyScripts, isBot ? 1000 : 3000);

    const loadHandler = function () {
      clearTimeout(idleTimer);
      loadHeavyScripts();
      ['scroll', 'click', 'touchstart', 'mousemove'].forEach(event => {
        window.removeEventListener(event, loadHandler);
      });
    };

    ['scroll', 'click', 'touchstart', 'mousemove'].forEach(event => {
      window.addEventListener(event, loadHandler, { passive: true, once: true });
    });
  }

  /**
   * Reduce layout shifts by reserving space for images
   */
  function preventLayoutShifts() {
    const images = document.querySelectorAll('img[width][height]');
    images.forEach(img => {
      if (!img.style.aspectRatio && img.width && img.height) {
        img.style.aspectRatio = `${img.width} / ${img.height}`;
      }
    });
  }

  /**
   * Optimize animations on mobile — reduce paint/layout work
   */
  function optimizeAnimations() {
    if (!isMobile) return;

    // Reduce motion if user prefers
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.setProperty('--animation-duration', '0.01ms');
    }

    // On mobile, use IntersectionObserver to throttle scroll-reveal animations
    if ('IntersectionObserver' in window) {
      const animEls = document.querySelectorAll('[data-animate]:not([data-animate-started])');
      const animObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-animate-started', '1');
            animObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '50px' });
      animEls.forEach(el => animObserver.observe(el));
    }
  }

  /**
   * Initialize all optimizations
   */
  function init() {
    // Run immediately for critical path
    preventLayoutShifts();
    optimizeAnimations();

    // Boost LCP as early as possible
    boostLCP();

    // We run optimizeImages here but it will catch above-fold ones
    optimizeImages();

    // Run remaining after DOM content or immediately if already loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        boostLCP(); // Run again after DOM is full
        setupIntersectionObserver();
        optimizeThirdPartyScripts();
        deferNonCriticalCSS();
      });
    } else {
      boostLCP();
      setupIntersectionObserver();
      optimizeThirdPartyScripts();
      deferNonCriticalCSS();
    }
  }

  // Start optimizations
  init();

  // Expose for manual triggering
  window.mobilePerformance = {
    loadScripts: loadHeavyScripts,
    optimizeImages: optimizeImages,
    boostLCP: boostLCP
  };

})();
